import type { TopCategoryViewModel } from 'api/types/top-category';
import type { ProductTypeViewModel } from 'api/types/product-type';
import type { SpecConstraintViewModel } from 'api/types/product-type/SpecConstraintViewModel';
import type { ConditionViewModel } from 'api/types/condition';
import type { CityViewModel } from 'api/types/city';
import type { DistrictViewModel } from 'api/types/district';
import React, { useState } from 'react';
import type { ListingFilterState, SpecOperator } from 'types/listingFilterState';
import { SpecValueType } from 'api/types/product-type/SpecConstraintViewModel';
import Button from 'components/Button';
import { iconMap } from 'utils/constants/iconMap';

type FilterPanelKey = 'specs' | 'durum' | 'konum' | 'fiyat';

const NUMERIC_SPEC_OPTIONS: { value: SpecOperator; label: string }[] = [
  { value: '>=', label: 'Min' },
  { value: '<=', label: 'Max' },
  { value: '=', label: 'Tam' },
];



export interface AdFilterProps {
  topCategories: TopCategoryViewModel[];
  secondaryCategories: { id: number; name?: string | null }[];
  productTypes: ProductTypeViewModel[];
  constraints: SpecConstraintViewModel[];
  conditions: ConditionViewModel[];
  cities: CityViewModel[];
  districts: DistrictViewModel[];
  draft: ListingFilterState;
  setDraft: React.Dispatch<React.SetStateAction<ListingFilterState>>;
  setDraftCategory: (level: 'top' | 'secondary' | 'productType', id: number | null) => void;
  setDraftSpecValue: (key: string, value: string) => void;
  setDraftSpecOperator: (key: string, operator: SpecOperator) => void;
  setDraftPrice: (field: 'lowerBound' | 'upperBound' | 'exactMatch', value: number | null) => void;
  onApply: () => void;
  onClearFilters: () => void;
}

export default function AdFilter({
  topCategories,
  secondaryCategories,
  productTypes,
  constraints,
  conditions,
  cities,
  districts,
  draft,
  setDraft,
  setDraftCategory,
  setDraftSpecValue,
  setDraftSpecOperator,
  setDraftPrice,
  onApply,
  onClearFilters,
}: AdFilterProps) {
  const [openPanels, setOpenPanels] = useState<Record<FilterPanelKey, boolean>>({
    specs: false,
    durum: false,
    konum: false,
    fiyat: false,
  });
  const togglePanel = (key: FilterPanelKey) => {
    setOpenPanels((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="card mb-4">
      <div className="card-body">
        <h5 className="card-title mb-3 text-primary">
          <i className="fas fa-filter me-2" />
          Filtreler
        </h5>

        {/* Kategoriler */}
        <div className="category-section mb-4">
          <h6 className="fw-bold border-bottom pb-2 mb-3">Kategoriler</h6>
          <div className="list-group">
            <Button
              type="button"
              className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${draft.topCategoryId === null ? 'active' : ''}`}
              onClick={() => setDraftCategory('top', null)}
            >
              Tüm Kategoriler
              <i className="fas fa-chevron-right text-muted" />
            </Button>
            {topCategories.map((top) => (
              <div key={top.id} className="category-group">
                <Button
                  type="button"
                  className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${draft.topCategoryId === top.id ? 'active' : ''}`}
                  onClick={() => setDraftCategory('top', top.id)}
                >
                  {top.name ?? `Kategori ${top.id}`}
                  <i className="fas fa-chevron-right text-muted" />
                </Button>
                {draft.topCategoryId === top.id && secondaryCategories.length > 1 && (
                  <>
                    {secondaryCategories.map((sec) => (
                      <div key={sec.id}>
                        <Button
                          type="button"
                          className={`list-group-item list-group-item-action ps-4 ${draft.secondaryCategoryId === sec.id ? 'active fw-medium' : 'text-muted'}`}
                          onClick={() => setDraftCategory('secondary', sec.id)}
                        >
                          <i className="fas fa-angle-right me-2" />
                          {sec.name ?? `Alt ${sec.id}`}
                        </Button>
                        {draft.secondaryCategoryId === sec.id && productTypes.length > 1 && (
                          <>
                            {productTypes.map((pt) => (
                              <Button
                                key={pt.id}
                                type="button"
                                className={`list-group-item list-group-item-action ps-5 small ${draft.productTypeId === pt.id ? 'active fw-medium' : 'text-muted'}`}
                                onClick={() => setDraftCategory('productType', pt.id)}
                              >
                                <i className={`${iconMap[pt.id] ?? "fas fa-microchip"} me-2`} />   {/* Add icon name here */}
                                {pt.name ?? `Ürün ${pt.id}`}
                              </Button>
                            ))}
                          </>
                        )}
                        {draft.secondaryCategoryId === sec.id && productTypes.length === 1 && (
                          <Button
                            type="button"
                            className="list-group-item list-group-item-action ps-5 small active fw-medium"
                            onClick={() => setDraftCategory('productType', productTypes[0].id)}
                          >
                            <i className={`${iconMap[productTypes[0].id] ?? "fas fa-microchip"} me-2`} /> {/* Add icon name here */}
                            {productTypes[0].name ?? `Ürün ${productTypes[0].id}`}
                          </Button>
                        )}
                      </div>
                    ))}
                  </>
                )}
                {draft.topCategoryId === top.id && secondaryCategories.length === 1 && (
                  <>
                    <Button
                      type="button"
                      className="list-group-item list-group-item-action ps-4 active fw-medium"
                      onClick={() => setDraftCategory('secondary', secondaryCategories[0].id)}
                    >
                      <i className="fas fa-angle-right me-2" />
                      {secondaryCategories[0].name ?? `Alt ${secondaryCategories[0].id}`}
                    </Button>
                    {draft.secondaryCategoryId === secondaryCategories[0].id && productTypes.length > 1 && (
                      <>
                        {productTypes.map((pt) => (
                          <Button
                            key={pt.id}
                            type="button"
                            className={`list-group-item list-group-item-action ps-5 small ${draft.productTypeId === pt.id ? 'active fw-medium' : 'text-muted'}`}
                            onClick={() => setDraftCategory('productType', pt.id)}
                          >
                            <i className="fas fa-microchip me-2" />
                            {pt.name ?? `Ürün ${pt.id}`}
                          </Button>
                        ))}
                      </>
                    )}
                    {draft.secondaryCategoryId === secondaryCategories[0].id && productTypes.length === 1 && (
                      <Button
                        type="button"
                        className="list-group-item list-group-item-action ps-5 small active fw-medium"
                        onClick={() => setDraftCategory('productType', productTypes[0].id)}
                      >
                        <i className="fas fa-microchip me-2" />
                        {productTypes[0].name ?? `Ürün ${productTypes[0].id}`}
                      </Button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Spec constraints (below category) */}
        {constraints.length > 0 && (
          <div className="spec-filters mb-4">
            <h6 className="fw-bold border-bottom pb-2 mb-3">Özellikler</h6>
            <div className="accordion mb-3">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Button
                    type="button"
                    className={`accordion-button ${openPanels.specs ? '' : 'collapsed'}`}
                    onClick={() => togglePanel('specs')}
                    aria-expanded={openPanels.specs}
                  >
                    Özellik filtreleri
                  </Button>
                </h2>
                <div className={`accordion-collapse collapse ${openPanels.specs ? 'show' : ''}`}>
                  <div className="accordion-body">
                    {constraints.map((c) => (
                      <div key={c.id} className="mb-3">
                        <label className="form-label mb-1">{c.displayName}</label>
                        {c.valueType === SpecValueType.Number && (
                          <>
                            <div className="d-flex gap-2 mb-1">
                              <select
                                className="form-select form-select-sm"
                                style={{ maxWidth: '100px' }}
                                value={draft.specOperators[c.key] ?? '='}
                                onChange={(e) => setDraftSpecOperator(c.key, e.target.value as SpecOperator)}
                              >
                                {NUMERIC_SPEC_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                className="form-control form-control-sm flex-grow-1"
                                value={draft.specValues[c.key] ?? ''}
                                onChange={(e) => setDraftSpecValue(c.key, e.target.value)}
                              />
                            </div>
                          </>
                        )}
                        {c.valueType === SpecValueType.String && (
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={draft.specValues[c.key] ?? ''}
                            onChange={(e) => setDraftSpecValue(c.key, e.target.value)}
                          />
                        )}
                        {c.valueType === SpecValueType.Enum && (
                          <select
                            className="form-select form-select-sm"
                            value={draft.specValues[c.key] ?? ''}
                            onChange={(e) => setDraftSpecValue(c.key, e.target.value)}
                          >
                            <option value="">Tümü</option>
                            {(c.allowedValues ?? []).map((av) => (
                              <option key={av.id} value={av.value}>
                                {av.value}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Non-spec filters */}
        <div className="filter-options-section">
          <h6 className="fw-bold border-bottom pb-2 mb-3">Filtreleme Seçenekleri</h6>

          <div className="accordion mb-3">
            <div className="accordion-item">
              <h2 className="accordion-header">
                <Button
                  type="button"
                  className={`accordion-button ${openPanels.durum ? '' : 'collapsed'}`}
                  onClick={() => togglePanel('durum')}
                  aria-expanded={openPanels.durum}
                >
                  Durum
                </Button>
              </h2>
              <div className={`accordion-collapse collapse ${openPanels.durum ? 'show' : ''}`}>
                <div className="accordion-body">
                  <select
                    className="form-select"
                    value={draft.conditionId ?? ''}
                    onChange={(e) => setDraft((prev) => ({ ...prev, conditionId: e.target.value === '' ? null : Number(e.target.value) }))}
                  >
                    <option value="">Tümü</option>
                    {conditions.map((c) => (
                      <option key={c.id} value={c.id}>{c.name ?? c.id}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="accordion mb-3">
            <div className="accordion-item">
              <h2 className="accordion-header">
                <Button
                  type="button"
                  className={`accordion-button ${openPanels.konum ? '' : 'collapsed'}`}
                  onClick={() => togglePanel('konum')}
                  aria-expanded={openPanels.konum}
                >
                  Konum
                </Button>
              </h2>
              <div className={`accordion-collapse collapse ${openPanels.konum ? 'show' : ''}`}>
                <div className="accordion-body">
                <select
                  className="form-select mb-2"
                  value={draft.cityId ?? ''}
                  onChange={(e) => setDraft((prev) => ({ ...prev, cityId: e.target.value === '' ? null : Number(e.target.value), districtId: null }))}
                >
                  <option value="">Tüm Şehirler</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.id}>{c.name ?? c.id}</option>
                  ))}
                </select>
                {draft.cityId != null && (
                  <select
                    className="form-select"
                    value={draft.districtId ?? ''}
                    onChange={(e) => setDraft((prev) => ({ ...prev, districtId: e.target.value === '' ? null : Number(e.target.value) }))}
                  >
                    <option value="">Tüm İlçeler</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>{d.name ?? d.id}</option>
                    ))}
                  </select>
                )}
                </div>
              </div>
            </div>
          </div>

          <div className="accordion mb-3">
            <div className="accordion-item">
              <h2 className="accordion-header">
                <Button
                  type="button"
                  className={`accordion-button ${openPanels.fiyat ? '' : 'collapsed'}`}
                  onClick={() => togglePanel('fiyat')}
                  aria-expanded={openPanels.fiyat}
                >
                  Fiyat Aralığı
                </Button>
              </h2>
              <div className={`accordion-collapse collapse ${openPanels.fiyat ? 'show' : ''}`}>
                <div className="accordion-body">
                  <div className="input-group mb-2">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Min TL"
                      value={draft.price.lowerBound ?? ''}
                      onChange={(e) => setDraftPrice('lowerBound', e.target.value === '' ? null : Number(e.target.value))}
                    />
                  </div>
                  <div className="input-group mb-2">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Max TL"
                      value={draft.price.upperBound ?? ''}
                      onChange={(e) => setDraftPrice('upperBound', e.target.value === '' ? null : Number(e.target.value))}
                    />
                  </div>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Tam fiyat (TL)"
                      value={draft.price.exactMatch ?? ''}
                      onChange={(e) => setDraftPrice('exactMatch', e.target.value === '' ? null : Number(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Button type="button" className="btn btn-success w-100 mt-3" onClick={onApply}>
          Filtreleri Uygula
        </Button>
        <Button type="button" className="btn btn-outline-secondary w-100 mt-2" onClick={onClearFilters}>
          Filtreleri Temizle
        </Button>
      </div>
    </div>
  );
}
